import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { generateBreadcrumbSchema } from "@/lib/structuredData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Phone, Calendar, MessageCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const services = [
  "Monthly Bookkeeping",
  "Payroll Services",
  "Tax Returns",
  "Income Tax",
  "Provisional Tax",
  "Annual Financial Statements",
  "CIPC Annual Returns",
  "Custom Advisory",
];

const BookACall = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    turnover: "",
    employees: "",
    contactMethod: "call",
    preferredDate: "",
    message: "",
  });

  const handleServiceToggle = (service: string) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          formType: "booking",
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          turnover: formData.turnover,
          employees: formData.employees,
          services: selectedServices,
          contactMethod: formData.contactMethod,
          preferredDate: formData.preferredDate,
          message: formData.message,
        },
      });

      if (error) throw error;

      // Google Ads conversion tracking
      if (typeof (window as any).gtag === "function") {
        (window as any).gtag("event", "conversion", {
          send_to: "AW-17832469496/T4nwCJC5vP0bEPjHl7dC",
          value: 1.0,
          currency: "ZAR",
        });
      }

      navigate("/thank-you");
    } catch (error) {
      console.error("Error sending form:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact us via WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappNumber = "27732509394";
  const whatsappMessage = encodeURIComponent("Hi! I'd like to book a consultation.");

  return (
    <Layout>
      <SEO 
        title="Free Bookkeeping Consultation | Book a Call Today"
        description="Book a free consultation with a SAICA registered Chartered Accountant. We'll assess your bookkeeping needs and recommend the right package — response within 24 hours."
        canonical="/book-a-call"
        structuredData={generateBreadcrumbSchema([
          { name: "Home", url: "https://bookkeepingservices.co.za/" },
          { name: "Book a Call", url: "https://bookkeepingservices.co.za/book-a-call" }
        ])}
      />
      {/* Hero */}
      <section className="py-16 lg:py-24 section-accent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
              Book a Call
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Schedule a free consultation to discuss your bookkeeping needs. 
              We'll assess your requirements and recommend the right solution for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20 lg:py-28 section-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Quick Contact Options */}
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              <div className="card-elevated p-6 flex items-center gap-4">
                <div className="icon-container flex-shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Prefer to call?</h3>
                  <p className="text-sm text-muted-foreground">Fill the form and we'll call you</p>
                </div>
              </div>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card-elevated p-6 flex items-center gap-4 hover:shadow-card-hover transition-all"
              >
                <div className="w-14 h-14 rounded-xl bg-whatsapp/10 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-whatsapp" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">WhatsApp Us</h3>
                  <p className="text-sm text-muted-foreground">Quick response guaranteed</p>
                </div>
              </a>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-8 shadow-soft">
              <div className="flex items-center gap-3 mb-8">
                <Calendar className="w-6 h-6 text-primary" />
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  Book Your Consultation
                </h2>
              </div>

              {/* Personal Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+27 12 345 6789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your company name"
                  />
                </div>
              </div>

              {/* Business Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <Label>Annual Turnover Band</Label>
                  <Select
                    value={formData.turnover}
                    onValueChange={(value) => setFormData({ ...formData, turnover: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select turnover range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nil">Nil / No turnover yet</SelectItem>
                      <SelectItem value="under2.3m">Under R2.3 million</SelectItem>
                      <SelectItem value="2.3m-10m">R2.3m - R10 million</SelectItem>
                      <SelectItem value="over10m">Over R10 million</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Employees</Label>
                  <Select
                    value={formData.employees}
                    onValueChange={(value) => setFormData({ ...formData, employees: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee count" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No employees</SelectItem>
                      <SelectItem value="1-5">1-5 employees</SelectItem>
                      <SelectItem value="6-10">6-10 employees</SelectItem>
                      <SelectItem value="11+">11+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Services Needed */}
              <div className="mb-8">
                <Label className="mb-3 block">Services Needed</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {services.map((service) => (
                    <div key={service} className="flex items-center gap-3">
                      <Checkbox
                        id={service}
                        checked={selectedServices.includes(service)}
                        onCheckedChange={() => handleServiceToggle(service)}
                      />
                      <label
                        htmlFor={service}
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        {service}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Preference */}
              <div className="mb-8">
                <Label className="mb-3 block">Preferred Contact Method</Label>
                <RadioGroup
                  value={formData.contactMethod}
                  onValueChange={(value) => setFormData({ ...formData, contactMethod: value })}
                  className="flex flex-wrap gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="call" id="call" />
                    <label htmlFor="call" className="text-sm cursor-pointer">Phone Call</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="whatsapp" id="whatsapp" />
                    <label htmlFor="whatsapp" className="text-sm cursor-pointer">WhatsApp</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="email" id="email-option" />
                    <label htmlFor="email-option" className="text-sm cursor-pointer">Email</label>
                  </div>
                </RadioGroup>
              </div>

              {/* Preferred Date */}
              <div className="mb-8">
                <Label htmlFor="preferredDate">Preferred Date/Time</Label>
                <Input
                  id="preferredDate"
                  type="text"
                  value={formData.preferredDate}
                  onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                  placeholder="e.g., Weekday mornings, or specific date"
                  className="mt-2"
                />
              </div>

              {/* Additional Message */}
              <div className="mb-8">
                <Label htmlFor="message">Additional Information</Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us anything else about your business or requirements..."
                  className="mt-2"
                />
              </div>

              <p className="text-xs text-muted-foreground mb-6">
                Your information is kept confidential in accordance with POPIA.
              </p>

              <Button type="submit" className="btn-primary w-full h-12 text-base" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit Booking Request"}
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                We'll confirm your slot within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookACall;
